import { sellTicketStatusInversed } from '@teinor/erp/types/company/order/sell/sellTicket'
import { faPenCircle } from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-solid-svg-icons'
import { SellTicketLogModelType } from '@teinor/erp/types/company/order/sell/sellTicket/sellTicketLog'
import { Fragment } from 'react'
import { InputTableLayoutData } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import OrderStatusBar from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBar'
import { getOrderStatusBarType } from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBar/functions'
import OrderStatusBarDropDown from '../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderStatusBarDropDown'
import { displayDate } from '../../../../../../../../../utils/globals/date'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'


interface SellTicketStatusBarProps extends CreateStatusBarProps { 

}

const SellTicketStatusBar: React.FC<SellTicketStatusBarProps> = ({ completeData}) => {
    const { statuses } = useCreateStatusBar({ completeData })
    return (
        <OrderStatusBar statuses={statuses} currentOrderStatus={completeData?.header.status} getOrderStatusBarType={getOrderStatusBarType} isCanceled={completeData?.header?.status === sellTicketStatusInversed['refunded'] } />
    )
}

interface CreateStatusBarProps {
    completeData: InputTableLayoutData | null
}

const useCreateStatusBar = ({ completeData }: CreateStatusBarProps) => {
    const { t } = useTwinTranslation()
    const OrderLogs = completeData?.header?.OrderLogs
    let statuses: TwinDictionary[] = []
    const dictOrderLogs: TwinDictionary = {}
    for (const log of OrderLogs) {
        if (!dictOrderLogs[log.logType]) {
            dictOrderLogs[log.logType] = [log]
        } else {
            dictOrderLogs[log.logType] = [...dictOrderLogs[log.logType], log]
        }
    }
    statuses.push({
        id: 0,
        name: t('draft', 'Borrador'),
        logType: 0,
        color: 'bg-gray-33',
        icon: faPenCircle,
        className: 'text-gray-33',
        extraClassName: 'border border-gray-33',
        renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} /> : null
    })
    if (completeData?.header?.status === sellTicketStatusInversed['saved']) {
        statuses.push({
            id: 1,
            name: t('saved', 'Guardado'),
            logType: 1,
            color: 'bg-purple-86',
            icon: faPenCircle,
            className: 'text-purple-86',
            extraClassName: 'border border-gray-33',
            renderMessage: dictOrderLogs[1]?.length ? <RenderLogMessage logs={dictOrderLogs[1] || []} /> : null
        })
    }
    if (completeData?.header?.status === sellTicketStatusInversed['partialCharged'] || dictOrderLogs[3]?.length) {
        statuses.push({
            id: 2,
            name: t('parcialCharged', 'Cobrado parcialmente'),
            logType: 3,
            color: 'bg-yellow-B9',
            icon: faCheckCircle,
            className: 'text-yellow-B9',
            renderMessage: dictOrderLogs[3]?.length ? <RenderLogMessage logs={dictOrderLogs[3] || []} /> : null
        })
    }
    statuses.push({
        id: 3,
        name: t('charged', 'Cobrado'),
        logType: 2,
        color: 'bg-green-43',
        icon: faCheckCircle,
        className: 'text-green-43',
        renderMessage: dictOrderLogs[2]?.length ? <RenderLogMessage logs={dictOrderLogs[2] || []} /> : null
    })
    if (completeData?.header?.status === sellTicketStatusInversed['partialRefunded'] || dictOrderLogs[4]?.length) {
        statuses.push({
            id: 4,
            name: t('parcialRefunded', 'Devuelto parcialmente'),
            logType: 4,
            color: 'bg-yellow-B9',
            icon: faXmarkCircle,
            className: 'text-yellow-B9',
            renderMessage: dictOrderLogs[4]?.length ? <RenderLogMessage logs={dictOrderLogs[4] || []} /> : null
        })
    }
    if (completeData?.header?.status === sellTicketStatusInversed['refunded']) {
        statuses.push({
            id: 5,
            name: t('refunded', 'Devuelto'),
            renderMessage: dictOrderLogs[5]?.length ? <RenderLogMessage logs={dictOrderLogs[5] || []} /> : null,
            logType: 5,
            color: 'bg-red-BA',
            icon: faXmarkCircle,
            className: 'text-red-BA',
        })
    }

    return { statuses }
}

interface RenderLogMessageProps {
    logs: SellTicketLogModelType[]
    QuoteOrderId?: number
    SellOrderId?: number
    SellDeliveryNoteId?: number
}

const RenderLogMessage: React.FC<RenderLogMessageProps> = ({ logs }) => {
    const { tVars } = useTwinTranslation()
    const renderMessage: JSX.Element[] = []
    for (const log of logs) {
        const myExtraData = log?.extraData ? JSON.parse(log?.extraData) : null
        const options = { employeeTrigger: log.EmployeeTrigger?.fullname_short || '', date: displayDate(new Date(log.createdAt)), full_assigned_number: myExtraData?.full_assigned_number || null, 'interpolation': { 'escapeValue': false }}
        const dictSellTicketLogsMessages: TwinDictionary = {
            0: tVars('createdTicketLogMessage', '{{employeeTrigger}} creó el ticket el {{date}}', options),
            1: tVars('savedTicketLogMessage', '{{employeeTrigger}} guardó el ticket el {{date}}', options),
            2: tVars('finishTicketLogMessage', '{{employeeTrigger}} marcó el ticket como cobrado el {{date}}', options),
            3: tVars('partialChargedTicketLogMessage', '{{employeeTrigger}} marcó el ticket como parcialmente cobrado el {{date}}', options),
            4: tVars('partialRefundedTicketLogMessage', '{{employeeTrigger}} marcó el ticket como parcialmente devuelto el {{date}}', options),
            5: tVars('refundedTicketLogMessage', '{{employeeTrigger}} marcó el ticket como devuelto el {{date}}', options),
        }
        if (dictSellTicketLogsMessages[log.logType]) {
            renderMessage.push(<div className='my-5' key={log.id} > {dictSellTicketLogsMessages[log.logType]} </div>)
        }
    }
    if (!renderMessage.length) {
        return null
    }
    return (<Fragment>{renderMessage}</Fragment>)
}

export const SellTicketStatusBarNew: React.FC<SellTicketStatusBarProps> = ({ completeData }) => {
    const { statuses } = useCreateStatusBar({ completeData })
    const sellTicketStatus = completeData?.header.status
    return (
        <OrderStatusBarDropDown statuses={statuses} currentOrderStatus={sellTicketStatus} getOrderStatusBarType={getOrderStatusBarType} isAccepted={sellTicketStatus === sellTicketStatusInversed['accepted'] || sellTicketStatus === sellTicketStatusInversed['partialCharged'] || sellTicketStatus === sellTicketStatusInversed['partialRefunded']} isCanceled={sellTicketStatus === sellTicketStatusInversed['refunded']} />
    )
}

export default SellTicketStatusBar